import React, { useState, useEffect } from 'react';
import { X, Check, AlertTriangle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { useTranslation } from '../lib/LanguageContext';
import { NotesMarkdown } from './NotesMarkdown';

interface SharedInboxProps {
  isOpen: boolean;
  onClose: () => void;
  projects: { id: string; name: string }[];
  onAccept: () => void;
}

interface SharedTodo {
  id: string;
  sender_email: string; // Removed sender_id as it doesn't exist in the table
  text: string;
  notes: string;
  message: string;
  created_at: string;
  status: 'pending' | 'accepted' | 'rejected';
  deadline: string | null;
}

export function SharedInbox({ isOpen, onClose, projects, onAccept }: SharedInboxProps) {
  const [sharedTodos, setSharedTodos] = useState<SharedTodo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProjects, setSelectedProjects] = useState<Record<string, string>>({});
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const { user } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen && user) {
      fetchSharedTodos();
    }
  }, [isOpen, user]);

  const fetchSharedTodos = async () => {
    if (!user?.email) return;
    
    try {
      setLoading(true);
      
      // First check that we have an authenticated session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        return;
      }
      
      if (!sessionData.session) {
        return;
      }
      
      // Make sure we're using lowercase email to match RLS policy
      const userEmail = user.email.toLowerCase();
      
      const { data, error } = await supabase
        .from('shared_todos')
        .select('id, sender_email, text, notes, message, created_at, status, deadline') // Removed sender_id, added deadline
        .eq('recipient_email', userEmail)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // No need to fetch emails separately since we have sender_email directly
      setSharedTodos((data || []) as SharedTodo[]);
    } catch (err) {
      setError(t('failedToLoadSharedNotes'));
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (id: string) => {
    const projectId = selectedProjects[id];

    if (!projectId) {
      setError(t('selectProjectForNote'));
      return;
    }
    
    try {
      setAcceptingId(id);
      
      // Get the shared todo data
      const { data: todoData, error: todoError } = await supabase
        .from('shared_todos')
        .select('*')
        .eq('id', id)
        .single();
        
      if (todoError) throw todoError;
      
      console.log('Shared todo data:', todoData);
      
      // Prepare the notes content (Markdown) with the message
      let formattedNotes = '';

      // Add the message to the notes if message exists
      if (todoData.message && todoData.message.trim() !== '') {
        formattedNotes = `**${t('messageFromSender')}** ${todoData.message}`;

        // Add a separator if there are additional notes
        if (todoData.notes && todoData.notes.trim() !== '') {
          formattedNotes += '\n\n---\n\n';
        }
      }

      // Add the original notes if they exist
      if (todoData.notes && todoData.notes.trim() !== '') {
        formattedNotes += todoData.notes;
      }
      
      console.log('Formatted notes to be saved:', formattedNotes);
      
      // Add to user's todos with properly formatted notes
      const newTodo = {
        user_id: user?.id,
        project_id: projectId,
        text: todoData.text,
        notes: formattedNotes,
        status: 'backlog',
        priority: false,
        deadline: todoData.deadline
      };
      
      console.log('Creating new todo with data:', newTodo);
      
      const { data: createdTodo, error: addError } = await supabase
        .from('todos')
        .insert([newTodo])
        .select()
        .single();
        
      if (addError) {
        console.error('Error creating todo:', addError);
        throw addError;
      }
      
      console.log('Created todo:', createdTodo);
      
      // For returned messages, delete the shared todo completely
      // For normal messages, update status to track acceptance
      if (todoData.message === 'Message returned to sender') {
        // Delete returned message - no need to track further
        const { error: deleteError } = await supabase
          .from('shared_todos')
          .delete()
          .eq('id', id);
          
        if (deleteError) {
          throw deleteError;
        }
      } else {
        // Update normal shared todo status and link to the newly created todo
        // Note: project_id is set for tracking but has no foreign key constraint
        // This allows projects to be deleted without cascade issues
        const updateData = { 
          status: 'accepted',
          project_id: projectId, // For tracking only (no FK constraint)
          accepted_at: new Date().toISOString(),
          todo_id: createdTodo.id // Link to the newly created todo
        };
        
        const { data: updatedTodo, error: updateError } = await supabase
          .from('shared_todos')
          .update(updateData)
          .eq('id', id)
          .select()
          .single();
          
        if (updateError) {
          throw updateError;
        }
      }
      
      // Remove from list
      setSharedTodos(prev => prev.filter(todo => todo.id !== id));
      onAccept();
    } catch (err) {
      console.error('Error accepting shared todo:', err);
      setError(t('failedToAccept'));
    } finally {
      setAcceptingId(null);
    }
  };

  const handleReject = async (id: string) => {
    try {
      setAcceptingId(id);
      
      // Get the shared todo data
      const { data: todoData, error: todoError } = await supabase
        .from('shared_todos')
        .select('*')
        .eq('id', id)
        .single();
        
      if (todoError) throw todoError;
      
      console.log('Using sender email for return:', todoData.sender_email);
      
      // Create a new shared todo that returns the sticky note to the original sender
      const returnTodo = {
        sender_email: user?.email,
        recipient_email: todoData.sender_email,
        text: todoData.text,
        notes: todoData.notes,
        message: 'Message returned to sender',
        status: 'pending',
        deadline: todoData.deadline
      };
      
      console.log('Creating return todo:', returnTodo);
      
      const { data: newSharedTodo, error: createError } = await supabase
        .from('shared_todos')
        .insert([returnTodo])
        .select()
        .single();
      
      if (createError) throw createError;
      
      console.log('Created return todo:', newSharedTodo);
      
      // Delete the original shared todo instead of marking as rejected
      // This prevents accumulation of rejected records in the database
      const { error: deleteError } = await supabase
        .from('shared_todos')
        .delete()
        .eq('id', id);
        
      if (deleteError) throw deleteError;
      
      // Remove from list
      setSharedTodos(prev => prev.filter(todo => todo.id !== id));
      onAccept(); // Refresh the inbox count
    } catch (err) {
      console.error('Error rejecting shared todo:', err);
      setError(t('failedToReject'));
    } finally {
      setAcceptingId(null);
    }
  };

  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
        <div className="flex justify-between items-center border-b px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('sharedTodos')}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-6 py-4 max-h-[50vh]">
          {loading ? (
            <div className="text-center py-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
              <p className="text-gray-500">{t('loadingSharedNotes')}</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 p-4 rounded-md text-red-700 mb-4">
              {error}
            </div>
          ) : sharedTodos.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <p>{t('noSharedNotes')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sharedTodos.map(todo => (
                <div key={todo.id} className={`border rounded-md p-4 ${
                  todo.message === 'Message returned to sender' 
                    ? 'bg-orange-50 border-orange-200' 
                    : 'bg-gray-50'
                }`}>
                  <div className="font-medium text-gray-800 mb-2">{todo.text}</div>
                  
                  {todo.message && (
                    <div className="mb-3 p-2 bg-indigo-50 rounded-md text-sm text-gray-700">
                      <p className="text-xs font-medium text-gray-500 mb-1">{t('messageLabel')}:</p>
                      {todo.message}
                    </div>
                  )}

                  <div className="text-xs text-gray-500 mb-3">
                    {t('from')}: {todo.sender_email || 'Unknown'} • {new Date(todo.created_at).toLocaleDateString()}
                  </div>

                  {todo.notes && (
                    <div className="mb-3">
                      <details className="text-sm">
                        <summary className="cursor-pointer text-indigo-600 hover:text-indigo-800">
                          {t('viewNotes')}
                        </summary>
                        <NotesMarkdown
                          notes={todo.notes}
                          className="mt-2 p-2 bg-white rounded text-xs text-gray-700"
                        />
                      </details>
                    </div>
                  )}

                  <div className="mt-4">
                    <label htmlFor={`project-${todo.id}`} className="block text-sm font-medium text-gray-700 mb-1">
                      {t('selectProject')}
                    </label>
                    <select
                      id={`project-${todo.id}`}
                      value={selectedProjects[todo.id] || ''}
                      onChange={(e) => setSelectedProjects({
                        ...selectedProjects,
                        [todo.id]: e.target.value
                      })}
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 text-sm"
                      required
                    >
                      <option value="">{t('selectProject')}</option>
                      {projects.map(project => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="mt-3 flex justify-end space-x-2">
                    {/* Hide reject button for returned messages to prevent infinite loops */}
                    {todo.message !== 'Message returned to sender' && (
                      <button
                        onClick={() => handleReject(todo.id)}
                        disabled={acceptingId === todo.id}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-red-600 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {acceptingId === todo.id ? (
                          <span>{t('processing')}</span>
                        ) : (
                          <>
                            <X className="h-4 w-4 mr-1" />
                            {t('reject')}
                          </>
                        )}
                      </button>
                    )}

                    <button
                      onClick={() => handleAccept(todo.id)}
                      disabled={!selectedProjects[todo.id] || acceptingId === todo.id}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {acceptingId === todo.id ? (
                        <span>{t('processing')}</span>
                      ) : (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          {t('accept')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="border-t px-6 py-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 w-full"
          >
            {t('close')}
          </button>
        </div>
      </div>
    </div>
  );
}